import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api from "@/lib/api";
import Navbar from "@/components/Navbar";
import DiffView from "@/components/DiffView";
import StatBox from "@/components/StatBox";
import CertificateBadge from "@/components/CertificateBadge";
import PersonalBestBadge from "@/components/PersonalBestBadge";
import KeyboardSoundToggle from "@/components/KeyboardSoundToggle";
import AdaptivePicks from "@/components/AdaptivePicks";
import RichTextEditor from "@/components/RichTextEditor";
import QueueBar from "@/components/QueueBar";
import { markDone } from "@/lib/practiceQueue";
import FormatHeatmap from "@/components/FormatHeatmap";
import KeyControlsPanel, { loadKeyControls } from "@/components/KeyControlsPanel";
import HighlightControlsPanel, { loadHighlightPrefs } from "@/components/HighlightControlsPanel";
import LiveHighlighter from "@/components/LiveHighlighter";
import LegalFontToggle, { loadLegalFont, legalFontClass } from "@/components/LegalFontToggle";
import SourceScrollNudge from "@/components/SourceScrollNudge";
import { useAutoScrollPref } from "@/lib/globalAutoScroll";
import { keyClickHandler } from "@/lib/keyboardSound";
import { compareFormatting } from "@/lib/formatDiff";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { showBadgeUnlocks, showMistakesAddedToast } from "@/lib/badgeToast";
import { RotateCcw, ArrowLeft, Send, Timer, Trophy, X, Eye, EyeOff,
  AlignLeft, AlignCenter, AlignRight, AlignJustify, Strikethrough } from "lucide-react";

const EXAM_SECONDS = 15 * 60; // 15 minutes — enforced minimum per exam board rules

function formatMMSS(s) {
  const m = Math.floor(s / 60).toString().padStart(2, "0");
  const sec = Math.floor(s % 60).toString().padStart(2, "0");
  return `${m}:${sec}`;
}

export default function LegalPractice() {
  const { id } = useParams();
  const [item, setItem] = useState(null);
  const [typed, setTyped] = useState("");
  const [start, setStart] = useState(null);
  const [elapsed, setElapsed] = useState(0);
  const [result, setResult] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const onKey = useMemo(() => keyClickHandler(), []);

  // Exam mode — always on for Legal (15-min official test timing).
  const [examMode] = useState(true);
  const [examRemaining, setExamRemaining] = useState(EXAM_SECONDS);
  const [examLocked, setExamLocked] = useState(false); // when time runs out
  const submittedRef = useRef(false);

  useEffect(() => {
    api.get(`/legal-passages/${id}`).then((r) => setItem(r.data)).catch(() => toast.error("Not found"));
  }, [id]);

  // Exam is auto-started on first keystroke via `handleType`. Reset re-arms it.

  useEffect(() => {
    if (!start) return;
    const t = setInterval(() => setElapsed((Date.now() - start) / 1000), 200);
    return () => clearInterval(t);
  }, [start]);

  const doSubmit = useCallback(async () => {
    if (submittedRef.current) return;
    if (!typed.trim()) { toast.error("Type something first"); return; }
    submittedRef.current = true;
    setSubmitting(true);
    try {
      const seconds = start ? (Date.now() - start) / 1000 : 60;
      const { data } = await api.post("/attempts", {
        module: "legal", item_id: id, typed_text: typed, time_seconds: seconds,
      });
      setResult(data);
      markDone("legal", id);
      // Client-side formatting score comparing the source HTML to the user's rich-text.
      try {
        const src = item?.content_html || item?.content || "";
        setFormatReport(compareFormatting(src, typedHtml));
      } catch { /* ignore */ }
      toast.success(`Accuracy: ${data.accuracy_percent}% · ${data.wpm} WPM`);
      showBadgeUnlocks(data.new_badges);
      showMistakesAddedToast(data.mistakes_added);
    } catch { toast.error("Submission failed"); submittedRef.current = false; }
    finally { setSubmitting(false); }
  }, [typed, start, id]);

  // Exam countdown
  useEffect(() => {
    if (!examMode || !start || examLocked || result) return;
    const t = setInterval(() => {
      setExamRemaining((prev) => {
        const remaining = EXAM_SECONDS - Math.floor((Date.now() - start) / 1000);
        if (remaining <= 0) {
          clearInterval(t);
          setExamLocked(true);
          toast.error("Time's up! Auto-submitting…");
          setTimeout(() => doSubmit(), 300);
          return 0;
        }
        return remaining;
      });
    }, 500);
    return () => clearInterval(t);
  }, [examMode, start, examLocked, result, doSubmit]);

  const [typedHtml, setTypedHtml] = useState("");
  const [formatReport, setFormatReport] = useState(null);
  const [liveCompare, setLiveCompare] = useState(false);
  const [keyControls, setKeyControls] = useState(() => loadKeyControls());
  const [highlightPrefs, setHighlightPrefs] = useState(() => loadHighlightPrefs());
  const [legalFont, setLegalFont] = useState(() => loadLegalFont());
  const [syncScroll, setSyncScroll] = useState(() => {
    // Default: OFF. Sync-scroll caps the source paper to viewport height and
    // adds an internal scrollbar so long judgements can be locked in step with
    // the typing panel. That hides content below the fold on first view (users
    // reported "content missing after 'List in due course'"), so we now start
    // students on the full-height paper and let them opt-in.
    try { return localStorage.getItem("stenosync.syncScroll.v2") === "on"; } catch { return false; }
  });
  // Auto-scroll follow — when ON (default), the source pane scrolls smoothly
  // so the paragraph the student is CURRENTLY typing stays visible near the
  // top of the pane. Off by default? No — this is the single biggest ergonomic
  // win for long judgements, so we ship it on. Users can flip it via the
  // toolbar toggle right next to Sync-Scroll.
  const [autoFollow, setAutoFollow] = useState(() => {
    try { return localStorage.getItem("stenosync.autoFollow.v1") !== "off"; } catch { return true; }
  });
  const [globalAutoScrollOn] = useAutoScrollPref();
  const sourceScrollRef = useRef(null);
  const editorScrollRef = useRef(null);

  // Top/bottom split — source pane height as % of the middle stack.
  // Persisted so students keep their preferred split across sessions.
  const [sourceHeight, setSourceHeight] = useState(() => {
    try {
      const v = parseFloat(localStorage.getItem("stenosync.legalSourceHeight.v1"));
      return Number.isFinite(v) && v >= 20 && v <= 75 ? v : 42;
    } catch { return 42; }
  });
  const stackRef = useRef(null);
  const startResize = (e) => {
    e.preventDefault();
    const stack = stackRef.current;
    if (!stack) return;
    const rect = stack.getBoundingClientRect();
    const onMove = (ev) => {
      const y = (ev.touches ? ev.touches[0].clientY : ev.clientY) - rect.top;
      const pct = Math.max(20, Math.min(75, (y / rect.height) * 100));
      setSourceHeight(pct);
    };
    const onUp = () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("touchmove", onMove);
      window.removeEventListener("mouseup", onUp);
      window.removeEventListener("touchend", onUp);
      try { localStorage.setItem("stenosync.legalSourceHeight.v1", String(sourceHeight)); } catch { /* ignore */ }
    };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("touchmove", onMove, { passive: false });
    window.addEventListener("mouseup", onUp);
    window.addEventListener("touchend", onUp);
  };
  useEffect(() => {
    try { localStorage.setItem("stenosync.legalSourceHeight.v1", String(sourceHeight)); } catch { /* ignore */ }
  }, [sourceHeight]);

  const toggleSyncScroll = () => {
    setSyncScroll((v) => {
      const next = !v;
      try { localStorage.setItem("stenosync.syncScroll.v2", next ? "on" : "off"); } catch { /* ignore */ }
      return next;
    });
  };
  const toggleAutoFollow = () => {
    setAutoFollow((v) => {
      const next = !v;
      try { localStorage.setItem("stenosync.autoFollow.v1", next ? "on" : "off"); } catch { /* ignore */ }
      return next;
    });
  };

  // Bind scroll listeners so scrolling one panel moves the other proportionally.
  // Uses a shared `lock` flag to break the ping-pong feedback loop; ratio is
  // scrollTop / (scrollHeight - clientHeight) so equal-percentage positions match.
  useEffect(() => {
    if (!syncScroll) return undefined;
    const src = sourceScrollRef.current;
    const ed = editorScrollRef.current;
    if (!src || !ed) return undefined;
    let lock = false;
    const mirror = (from, to) => {
      if (lock) return;
      const range = from.scrollHeight - from.clientHeight;
      if (range <= 0) return;
      const ratio = from.scrollTop / range;
      const toRange = to.scrollHeight - to.clientHeight;
      lock = true;
      to.scrollTop = ratio * toRange;
      requestAnimationFrame(() => { lock = false; });
    };
    const onSrc = () => mirror(src, ed);
    const onEd  = () => mirror(ed, src);
    src.addEventListener("scroll", onSrc, { passive: true });
    ed.addEventListener("scroll", onEd, { passive: true });
    return () => {
      src.removeEventListener("scroll", onSrc);
      ed.removeEventListener("scroll", onEd);
    };
  }, [syncScroll, item]);

  // ============ Auto-scroll follow ============
  // When the student types, walk the source's DOM until we find the block
  // element that contains the (typedWordCount)-th word. Then smoothly scroll
  // the source pane so that block sits ~25 % from the top of the viewport —
  // no more darting back to the top to find the next line. Disabled while
  // sync-scroll is ON so the two behaviours don't fight each other.
  useEffect(() => {
    // Guard both switches: local per-page Follow AND the global master toggle.
    if (!autoFollow || !globalAutoScrollOn || syncScroll) return;
    const container = sourceScrollRef.current;
    const sourceEl = container?.querySelector('[data-testid="legal-source-text"]');
    if (!container || !sourceEl) return;
    const typedCount = typed.trim() ? typed.trim().split(/\s+/).length : 0;
    if (typedCount === 0) {
      container.scrollTo({ top: 0, behavior: "smooth" });
      return;
    }
    // Walk text nodes, tally words, stop at the node containing the current word
    const walker = document.createTreeWalker(sourceEl, NodeFilter.SHOW_TEXT);
    let seen = 0;
    let targetBlock = null;
    while (walker.nextNode()) {
      const node = walker.currentNode;
      const matches = (node.textContent || "").match(/\S+/g);
      if (!matches) continue;
      if (seen + matches.length >= typedCount) {
        // Find nearest non-inline ancestor so we scroll to a paragraph, not a span
        let el = node.parentElement;
        while (el && el !== sourceEl) {
          const disp = getComputedStyle(el).display;
          if (disp !== "inline" && disp !== "inline-block") { targetBlock = el; break; }
          el = el.parentElement;
        }
        if (!targetBlock) targetBlock = node.parentElement;
        break;
      }
      seen += matches.length;
    }
    if (!targetBlock) return;
    // Compute a scrollTop that puts the block ~25 % from the pane top.
    const containerRect = container.getBoundingClientRect();
    const blockRect = targetBlock.getBoundingClientRect();
    const currentScroll = container.scrollTop;
    const desired = currentScroll + (blockRect.top - containerRect.top) - containerRect.height * 0.25;
    const clamped = Math.max(0, Math.min(desired, container.scrollHeight - container.clientHeight));
    // Only scroll if the block is far from the ideal position (avoid jitter on every keystroke)
    if (Math.abs(clamped - currentScroll) > 24) {
      container.scrollTo({ top: clamped, behavior: "smooth" });
    }
  }, [typed, autoFollow, globalAutoScrollOn, syncScroll, item]);

  const handleType = (html, plain) => {
    if (examLocked) return;
    setTypedHtml(html);
    setTyped(plain);
    if (!start) setStart(Date.now());
  };

  // Live formatting report — recomputes on every keystroke while `liveCompare` is on.
  const liveFormatReport = useMemo(() => {
    if (!liveCompare || !item) return null;
    try {
      const src = item.content_html || item.content || "";
      return compareFormatting(src, typedHtml);
    } catch { return null; }
  }, [liveCompare, typedHtml, item]);

  const startExam = () => {
    reset();
    setExamRemaining(EXAM_SECONDS);
    setStart(Date.now());
    toast.info("Exam started. 15:00 on the clock — submit anytime, auto-submits at 0:00.");
  };

  const cancelExam = () => {
    setExamLocked(false);
    reset();
  };

  const reset = () => {
    setTyped(""); setStart(null); setElapsed(0); setResult(null);
    setExamLocked(false); setExamRemaining(EXAM_SECONDS);
    submittedRef.current = false;
  };

  const typedWordCount = typed.trim() ? typed.trim().split(/\s+/).length : 0;
  const liveWpm = start && elapsed > 0 ? Math.round(typedWordCount / (elapsed / 60)) : 0;

  // Split "incorrect" words into spelling vs punctuation using a normalize-then-compare
  // heuristic — if two words match once punctuation is stripped, only punctuation differs.
  const errorBreakdown = useMemo(() => {
    if (!result?.diff?.words) return { spelling: 0, punctuation: 0 };
    let spelling = 0;
    let punctuation = 0;
    const punctRe = /[^\p{L}\p{N}]+/gu;
    for (const w of result.diff.words) {
      if (w.status !== "incorrect") continue;
      const a = (w.word || "").replace(punctRe, "").toLowerCase();
      const b = (w.typed || "").replace(punctRe, "").toLowerCase();
      if (a === b) punctuation++; else spelling++;
    }
    return { spelling, punctuation };
  }, [result]);

  // Formatting error count = number of failed format checks (bold/italic/underline/alignment mismatches).
  const formattingErrors = useMemo(() => {
    if (!formatReport?.checks) return 0;
    return formatReport.checks.filter((c) => !c.ok).length;
  }, [formatReport]);

  // Final score — weighted combo of word accuracy (70%) and formatting fidelity (30%) when we have both.
  const finalScore = useMemo(() => {
    if (!result) return 0;
    const acc = result.accuracy_percent || 0;
    if (formatReport?.score != null) {
      return Math.round(acc * 0.7 + formatReport.score * 0.3);
    }
    return Math.round(acc);
  }, [result, formatReport]);

  const scrollToMistakes = () => {
    setShowDrawer(true);
    setTimeout(() => {
      const el = document.querySelector('[data-testid="legal-diff-view"]') || document.querySelector('[data-testid="legal-result"]');
      if (el && el.scrollIntoView) el.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 100);
  };

  // Custom toolbar helpers — call document.execCommand on the currently focused editor.
  // The FmtBtn's onMouseDown={preventDefault} keeps caret focus inside the contentEditable.
  const execFmt = (cmd) => {
    document.execCommand(cmd, false);
    const el = document.querySelector('[data-testid="legal-textarea"]');
    el?.focus?.();
  };
  const makeUpper = () => {
    const sel = window.getSelection();
    if (!sel || sel.rangeCount === 0 || sel.isCollapsed) {
      // No selection — uppercase all typed text in the editor.
      const el = document.querySelector('[data-testid="legal-textarea"]');
      if (!el) return;
      el.focus();
      document.execCommand("selectAll");
    }
    const sel2 = window.getSelection();
    if (!sel2 || sel2.isCollapsed) return;
    const upper = sel2.toString().toUpperCase();
    document.execCommand("insertText", false, upper);
  };

  const timerLow = examMode && examRemaining <= 60;

  // Focus-mode drawer for secondary panels (highlight controls, key controls,
  // live compare, live progress, format heatmap). Auto-opens whenever the
  // practice result lands so the student sees their score without hunting for it.
  const [showDrawer, setShowDrawer] = useState(false);
  useEffect(() => { if (result) setShowDrawer(true); }, [result]);

  return (
    <div className="h-screen flex flex-col overflow-hidden bg-background text-foreground">
      {!item ? (
        <div className="flex-1 flex items-center justify-center font-mono text-muted-foreground">Loading…</div>
      ) : (
        <>
          {/* ============ TOP HEADER — Back arrow + Centered Timer only ============ */}
          <div className="flex-shrink-0 bg-[#1E3A8A] px-4 py-2 text-white relative">
            <Link
              to="/legal"
              className="absolute left-3 top-1/2 -translate-y-1/2 inline-flex items-center justify-center w-9 h-9 rounded-md text-white/90 hover:text-white hover:bg-white/10 transition-colors"
              data-testid="back-to-legal"
              aria-label="Back"
            >
              <ArrowLeft className="w-5 h-5" />
            </Link>
            <div className="text-center" data-testid="legal-header-timer">
              <div className="text-[10px] uppercase tracking-[0.3em] font-bold text-white/70 leading-none">Time Left</div>
              <div
                className={`font-mono text-xl md:text-2xl font-black tracking-tight tabular-nums leading-tight ${timerLow ? "text-brand-crimson animate-pulse" : "text-white"}`}
                data-testid="legal-exam-timer"
              >
                {(() => {
                  const remaining = start ? examRemaining : EXAM_SECONDS;
                  const m = Math.floor(remaining / 60);
                  const s = Math.floor(remaining % 60);
                  return `${String(m).padStart(2, "0")} mins : ${String(s).padStart(2, "0")} sec`;
                })()}
              </div>
            </div>
            {examLocked && (
              <div className="mt-2 border-l-4 border-brand-crimson bg-brand-crimson/20 px-3 py-1.5 rounded font-mono text-xs text-white" data-testid="legal-exam-timeup">
                ⏰ Time's up — typing locked and auto-submitted.
              </div>
            )}
          </div>

          {/* Queue bar — shown when a practice queue is active for legal */}
          <QueueBar module="legal" currentId={id} hrefFor={(nid) => `/legal/${nid}`} />

          {/* ============ MIDDLE — vertical stack (SOURCE ↑ / TYPING ↓)
              Full-bleed layout — content stretches edge-to-edge, no side gutters. */}
          <div className="flex-1 min-h-0">
            <div
              ref={stackRef}
              className="h-full w-full flex flex-col"
              data-testid="legal-stack-wrap"
            >
              {/* SOURCE PANE — top (READ-ONLY, no menus/toolbars) */}
              <div className="flex flex-col min-h-0" style={{ flex: `0 0 calc(${sourceHeight}% - 6px)` }}>
                <div className="flex-1 min-h-0 flex flex-col bg-white overflow-hidden">
                  <div
                    ref={sourceScrollRef}
                    className="flex-1 min-h-0 overflow-y-auto pane-scroll"
                    data-testid="legal-source-scroll"
                  >
                    <div
                      className={`legal-page legal-page--full practice-typing-area ${legalFontClass(legalFont)}`}
                      data-testid="legal-source-text"
                      data-font={legalFont}
                      style={{ lineHeight: 1.55, whiteSpace: "pre-wrap" }}
                    >
                      <div className="legal-page__watermark">{item.court || "Court judgement"}</div>
                      <div dangerouslySetInnerHTML={{ __html: item.content_html || item.content || "" }} />
                    </div>
                  </div>
                </div>
              </div>

              {/* Resize divider — drag to change split */}
              <div
                onMouseDown={startResize}
                onTouchStart={startResize}
                role="separator"
                aria-label="Resize source and typing panes"
                aria-orientation="horizontal"
                data-testid="legal-resize-divider"
                className="h-2 flex-shrink-0 flex items-center justify-center cursor-row-resize group select-none bg-slate-200"
              >
                <div className="w-24 h-0.5 rounded-full bg-slate-400 group-hover:bg-slate-600 transition-colors" />
              </div>

              {/* TYPING PANE — bottom (slim toolbar + editor) */}
              <div className="flex-1 min-h-0 flex flex-col bg-white overflow-hidden" data-testid="legal-editor-pane">
                {/* Slim single-row formatting toolbar */}
                <div className="flex-shrink-0 border-b border-slate-200 bg-slate-50 px-3 py-1.5" data-testid="legal-custom-toolbar">
                  <div className="flex items-center gap-1.5 flex-wrap">
                    <FmtBtn label="Bold" onClick={() => execFmt("bold")} testId="fmt-bold"><b className="text-sm font-black">B</b></FmtBtn>
                    <FmtBtn label="Italic" onClick={() => execFmt("italic")} testId="fmt-italic"><i className="text-sm font-black not-italic" style={{fontStyle:"italic"}}>I</i></FmtBtn>
                    <FmtBtn label="Underline" onClick={() => execFmt("underline")} testId="fmt-underline"><span className="text-sm font-black underline">U</span></FmtBtn>
                    <FmtBtn label="UPPERCASE selected text" onClick={makeUpper} testId="fmt-upper" wide>
                      <span className="text-[10px] font-black tracking-widest">A UPPER</span>
                    </FmtBtn>
                    <div className="w-px h-5 bg-slate-300 mx-0.5" />
                    <FmtBtn label="Left align" onClick={() => execFmt("justifyLeft")} testId="fmt-left"><AlignLeft className="w-3.5 h-3.5" /></FmtBtn>
                    <FmtBtn label="Center align" onClick={() => execFmt("justifyCenter")} testId="fmt-center"><AlignCenter className="w-3.5 h-3.5" /></FmtBtn>
                    <FmtBtn label="Right align" onClick={() => execFmt("justifyRight")} testId="fmt-right"><AlignRight className="w-3.5 h-3.5" /></FmtBtn>
                    <FmtBtn label="Justify" onClick={() => execFmt("justifyFull")} testId="fmt-justify"><AlignJustify className="w-3.5 h-3.5" /></FmtBtn>
                    <FmtBtn label="Strikethrough" onClick={() => execFmt("strikeThrough")} testId="fmt-strike"><Strikethrough className="w-3.5 h-3.5" /></FmtBtn>
                    <div className="w-px h-5 bg-slate-300 mx-0.5" />
                    <button
                      type="button"
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={toggleAutoFollow}
                      data-testid="legal-auto-follow-btn"
                      aria-pressed={autoFollow}
                      title="Auto-scroll the source paper to follow the paragraph you're typing"
                      className={`inline-flex items-center gap-1 h-8 px-2 rounded border text-[10px] uppercase tracking-widest font-black transition-colors ${
                        autoFollow
                          ? "border-brand-emerald bg-brand-emerald text-white"
                          : "border-slate-300 bg-white text-slate-700 hover:border-brand-emerald hover:text-brand-emerald"
                      }`}
                    >
                      ⟳ Auto-scroll {autoFollow ? "ON" : "OFF"}
                    </button>
                    <div className="ml-auto text-[10px] font-bold text-brand-crimson flex items-center gap-1" data-testid="kbd-shortcuts-warning">
                      <span className="text-sm leading-none">⊘</span> Keyboard Shortcuts Disabled
                    </div>
                  </div>
                </div>

                <RichTextEditor
                  value={typedHtml}
                  onChange={handleType}
                  onKeyDown={onKey}
                  disabled={examLocked}
                  placeholder={examMode ? "Type carefully — clock is running. Preserve every bold, italic and alignment from the source." : "Type the passage above exactly as it appears (including formatting)…"}
                  testId="legal-textarea"
                  size="legal"
                  layout="fill"
                  hideDefaultToolbar
                  wideCanvas
                  scrollRef={editorScrollRef}
                  keyControls={{ ...keyControls, controlKeys: false }}
                />
              </div>
            </div>
          </div>

          <SourceScrollNudge variant="floating" testId="legal-scroll-nudge" />

          {/* ============ BOTTOM ACTION BAR — Red Cancel + Submit only ============ */}
          <div className="flex-shrink-0 border-t border-border/60 bg-background/95 backdrop-blur px-4 py-3 order-last">
            <div className="max-w-none mx-auto flex items-center gap-3">
              <Button
                onClick={reset}
                className="rounded-lg h-11 px-6 bg-brand-crimson hover:bg-red-700 text-white text-xs uppercase tracking-[0.2em] font-black shadow-md"
                data-testid="legal-cancel-btn"
              >
                <X className="w-4 h-4 mr-2" /> Cancel
              </Button>
              <div className="flex-1" />
              {result && (
                <button
                  type="button"
                  onClick={() => setShowDrawer((v) => !v)}
                  className="hidden md:inline-flex items-center gap-1.5 text-[10px] uppercase tracking-widest font-bold text-muted-foreground hover:text-foreground px-3 h-9"
                  data-testid="legal-drawer-toggle"
                >
                  {showDrawer ? "Hide results" : "Show results"}
                </button>
              )}
              <Button
                onClick={doSubmit}
                disabled={submitting || examLocked || !!result}
                className="rounded-lg h-11 px-8 bg-gradient-to-r from-[#E5C158] to-[#D4AF37] hover:from-[#D4AF37] hover:to-[#B8860B] text-[#0F172A] text-xs uppercase tracking-[0.2em] font-black shadow-lg shadow-brand-gold/25"
                data-testid="legal-submit-btn"
              >
                <Send className="w-4 h-4 mr-2" /> {submitting ? "Checking…" : "Submit"}
              </Button>
            </div>
          </div>

          {/* ============ BOTTOM DRAWER — controls, live progress, results ============
              Sits INSIDE the fullscreen flex column (not fixed-position) so opening
              it shrinks the split pane instead of hiding the action bar. */}
          {showDrawer && (
            <div
              className="flex-shrink-0 bg-background border-t-2 border-brand-gold shadow-2xl flex flex-col"
              style={{ maxHeight: "45vh" }}
              data-testid="legal-drawer"
            >
              <div className="flex-shrink-0 flex items-center justify-between px-4 py-1.5 border-b border-border">
                <div className="text-[10px] uppercase tracking-[0.3em] font-bold text-brand-gold">
                  {result ? "Results & tools" : "Practice tools"}
                </div>
                <button
                  type="button"
                  onClick={() => setShowDrawer(false)}
                  className="p-1 rounded hover:bg-secondary"
                  data-testid="legal-drawer-close"
                  title="Close"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
              <div className="flex-1 min-h-0 overflow-y-auto px-4 py-3 space-y-4">
                {/* Live formatting fidelity */}
                {liveCompare && liveFormatReport && liveFormatReport.checks.length > 0 && (
                  <div className="border-2 border-brand-gold/30 rounded-xl p-3 bg-brand-gold/5" data-testid="legal-live-format-report" aria-live="polite">
                    <div className="flex items-center justify-between mb-2">
                      <div className="text-[10px] uppercase tracking-[0.3em] font-bold text-brand-gold">Live formatting fidelity</div>
                      <div className="font-mono text-sm font-black text-brand-gold" data-testid="legal-live-format-score">{liveFormatReport.score}% match</div>
                    </div>
                    <ul className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
                      {liveFormatReport.checks.map((c) => (
                        <li
                          key={c.id}
                          className={`flex items-center gap-2 px-2.5 py-1 border rounded text-xs font-mono transition-colors ${
                            c.ok ? "border-brand-emerald/40 bg-brand-emerald/10 text-brand-emerald" : "border-brand-crimson/40 bg-brand-crimson/10 text-brand-crimson"
                          }`}
                          data-testid={`legal-live-format-check-${c.id}`}
                        >
                          <span className="text-base font-black">{c.ok ? "✓" : "×"}</span>
                          <span className="font-bold">{c.label}</span>
                          <span className="ml-auto text-[9px] uppercase tracking-widest opacity-70">
                            {c.expected && !c.actual ? "missing" : !c.expected && c.actual ? "extra" : "ok"}
                          </span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {liveCompare && item && (item.content_html || item.content) && (
                  <FormatHeatmap sourceHtml={item.content_html || item.content || ""} typedHtml={typedHtml} />
                )}

                {/* Live word-highlight preview */}
                {(highlightPrefs.wordHighlight || highlightPrefs.mistakeHighlight) && start && !result && (
                  <div className="border border-border bg-background p-3 max-h-40 overflow-y-auto rounded-lg" data-testid="legal-live-progress-wrap">
                    <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-muted-foreground mb-2">Live progress</div>
                    <LiveHighlighter
                      source={item.content || ""}
                      typed={typed}
                      lang="en"
                      wordHighlight={highlightPrefs.wordHighlight}
                      mistakeHighlight={highlightPrefs.mistakeHighlight}
                      autoScroll={highlightPrefs.autoScroll}
                    />
                  </div>
                )}

                {/* Highlight & key control panels */}
                <div className="grid gap-3 md:grid-cols-2">
                  <HighlightControlsPanel value={highlightPrefs} onChange={setHighlightPrefs} testId="legal-highlight-controls" />
                  <KeyControlsPanel value={keyControls} onChange={setKeyControls} testId="legal-key-controls" />
                </div>

                {/* Results — shown once available */}
                {result && (
                  <section className="pt-2 border-t border-border" data-testid="legal-result">
                    <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
                      <h2 className="text-xl font-black tracking-tight flex items-center gap-3">
                        Results {examMode && <span className="text-[10px] px-2 py-0.5 bg-brand-crimson text-white uppercase tracking-widest font-bold rounded">Exam</span>}
                      </h2>
                      <div className="flex items-center gap-3">
                        <div className="text-right">
                          <div className="text-[9px] uppercase tracking-[0.3em] font-bold text-muted-foreground">Final score</div>
                          <div className={`font-mono text-3xl font-black tracking-tight ${finalScore >= 90 ? "text-brand-emerald" : finalScore >= 70 ? "text-brand-gold" : "text-brand-crimson"}`} data-testid="legal-final-score">
                            {finalScore}<span className="text-base text-muted-foreground">/100</span>
                          </div>
                        </div>
                        <Button
                          type="button"
                          onClick={scrollToMistakes}
                          className="rounded-lg h-10 px-4 bg-brand-gold hover:bg-brand-gold/90 text-[#0F172A] text-[10px] uppercase tracking-widest font-black"
                          data-testid="legal-view-mistakes-btn"
                        >
                          <Eye className="w-3.5 h-3.5 mr-1.5" /> View Mistakes
                        </Button>
                      </div>
                    </div>

                    {/* Detailed metrics grid — 10 tiles */}
                    <div className="grid grid-cols-2 md:grid-cols-5 gap-2 mb-4" data-testid="legal-result-breakdown">
                      <StatBox testId="legal-result-total-words" label="Total Words" value={result.total_words} tone="default" />
                      <StatBox testId="legal-result-correct" label="Correct" value={result.correct} tone="good" />
                      <StatBox testId="legal-result-wrong" label="Wrong" value={result.incorrect} tone="bad" />
                      <StatBox testId="legal-result-missing" label="Missing" value={result.missing} tone="warn" />
                      <StatBox testId="legal-result-extra" label="Extra" value={result.extra} tone="warn" />
                      <StatBox testId="legal-result-spelling" label="Spelling" value={errorBreakdown.spelling} tone="bad" />
                      <StatBox testId="legal-result-punct" label="Punctuation" value={errorBreakdown.punctuation} tone="warn" />
                      <StatBox testId="legal-result-formatting" label="Formatting" value={formattingErrors} tone={formattingErrors === 0 ? "good" : "bad"} />
                      <StatBox testId="legal-result-accuracy" label="Accuracy" value={result.accuracy_percent} unit="%" tone={result.accuracy_percent >= 90 ? "good" : result.accuracy_percent >= 70 ? "warn" : "bad"} />
                      <StatBox testId="legal-result-wpm" label="WPM" value={result.wpm} unit="wpm" tone="accent" />
                    </div>

                    <div className="grid grid-cols-2 gap-2 mb-4 md:grid-cols-4">
                      <StatBox testId="legal-result-time" label="Time Taken" value={formatMMSS(elapsed)} unit="" tone="default" />
                      <StatBox testId="legal-result-error-pct" label="Errors" value={result.error_percent} unit="%" tone="bad" />
                      <StatBox testId="legal-result-typed-words" label="Typed" value={result.diff?.typed_words ?? result.total_words} tone="default" />
                      <StatBox testId="legal-result-format-match" label="Format Match" value={formatReport?.score ?? "—"} unit={formatReport ? "%" : ""} tone={formatReport?.score >= 90 ? "good" : "warn"} />
                    </div>

                    <CertificateBadge attemptId={result.id} accuracy={result.accuracy_percent} wpm={result.wpm} />
                    <PersonalBestBadge result={result} />
                    <div className="mt-4" data-testid="legal-diff-view">
                      <DiffView
                        result={result.diff}
                        sourceHtml={item.content_html || item.content || ""}
                        typedHtml={typedHtml}
                      />
                    </div>
                    {formatReport && formatReport.checks.length > 0 && (
                      <div className="mt-4 border-2 border-border rounded-2xl p-4 bg-secondary/40" data-testid="legal-format-report">
                        <div className="flex items-center justify-between mb-3">
                          <div>
                            <div className="text-[10px] uppercase tracking-[0.3em] font-bold text-brand-gold mb-1">Formatting fidelity</div>
                            <div className="font-display text-xl font-black tracking-tight">
                              {formatReport.score}<span className="text-muted-foreground text-base">% formatting match</span>
                            </div>
                          </div>
                        </div>
                        <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                          {formatReport.checks.map((c) => (
                            <li key={c.id} className={`flex items-center gap-2 px-3 py-1.5 border rounded-lg ${c.ok ? "border-brand-emerald/40 bg-brand-emerald/10 text-brand-emerald" : "border-brand-crimson/40 bg-brand-crimson/10 text-brand-crimson"}`} data-testid={`legal-format-check-${c.id}`}>
                              <span className="font-mono text-lg">{c.ok ? "✓" : "×"}</span>
                              <span className="text-sm font-bold">{c.label}</span>
                              <span className="ml-auto text-[10px] font-mono uppercase tracking-widest opacity-70">
                                {c.expected ? "expected" : "not in source"}
                              </span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                    <div className="mt-6">
                      <AdaptivePicks module="legal" limit={3} title="Try next" />
                    </div>
                  </section>
                )}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

/**
 * Compact mini stat pill used in the top strip so we can keep the fullscreen
 * layout in a single-row header without eating vertical space.
 */
function MiniStat({ testId, label, value, unit, tone = "default" }) {
  const toneClass =
    tone === "accent" ? "text-brand-gold border-brand-gold/40" :
    tone === "good" ? "text-brand-emerald border-brand-emerald/40" :
    tone === "warn" ? "text-brand-gold border-brand-gold/40" :
    tone === "bad" ? "text-brand-crimson border-brand-crimson/40" :
    "text-foreground border-border";
  return (
    <div
      data-testid={testId}
      className={`hidden sm:inline-flex items-baseline gap-1 border px-2 py-1 rounded-md font-mono text-xs ${toneClass}`}
    >
      <span className="text-[9px] uppercase tracking-widest text-muted-foreground">{label}</span>
      <span className="font-black">{value}</span>
      {unit && <span className="text-[9px] text-muted-foreground">{unit}</span>}
    </div>
  );
}

/** Formatting toolbar button — compact 32px, hover gold, keeps caret in editor via onMouseDown preventDefault. */
function FmtBtn({ children, onClick, label, testId, wide = false }) {
  return (
    <button
      type="button"
      onMouseDown={(e) => e.preventDefault()}
      onClick={onClick}
      data-testid={testId}
      title={label}
      aria-label={label}
      className={`inline-flex items-center justify-center rounded border border-slate-300 bg-white hover:bg-brand-gold hover:text-[#0F172A] hover:border-brand-gold text-slate-800 transition-colors ${wide ? "h-8 px-3" : "h-8 w-8"}`}
    >
      {children}
    </button>
  );
}
